import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Avatar,
  Badge,
  BadgeProps,
  Box,
  Card,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'

import CancelIcon from '@mui/icons-material/Cancel'

import responsiveHook from '@hooks/useResponsive'

import {
  avatarSx,
  badgeSx,
  badgeValueSx,
  bubbleSx,
  bubbleWrapperSx,
  cancelBackgroundSx,
  closeButtonSx,
  containerSx,
} from './styles'

const defaultBadgeProps: BadgeProps = {
  overlap: 'circular',
  anchorOrigin: { vertical: 'top', horizontal: 'right' },
  color: 'error',
}

enum BubbleDisplayState {
  HIDDEN,
  VISIBLE,
  CLOSED,
}

export interface IAssistanceChatBubble {
  text: string
  waitTime?: number
  hideBadgeOnZeroValue?: boolean
  badgeValue?: number
  badgeProps?: BadgeProps
  bubbleSxProps?: SxProps<Theme>
  onInteract?: () => void
}

const AssistanceChatBubble = ({
  text,
  waitTime = 3,
  hideBadgeOnZeroValue = true,
  badgeValue = 1,
  badgeProps = { ...defaultBadgeProps },
  bubbleSxProps,
  onInteract,
}: IAssistanceChatBubble) => {
  const { isMobile } = responsiveHook.useResponsive()

  const containerRef = useRef<HTMLDivElement>(null)

  const [chatBubbleDisplayState, setChatBubbleDisplayState] = useState(
    BubbleDisplayState.HIDDEN
  )
  const { t } = useTranslation()

  const handleOnClose = () => {
    setChatBubbleDisplayState(BubbleDisplayState.CLOSED)
  }

  const openAtWaitTime = useCallback(() => {
    setTimeout(() => {
      setChatBubbleDisplayState(BubbleDisplayState.VISIBLE)
    }, waitTime * 1000)
  }, [waitTime])

  useEffect(() => {
    openAtWaitTime()
  }, [openAtWaitTime])

  return (
    <Stack
      ref={containerRef}
      sx={containerSx({
        isVisible: chatBubbleDisplayState !== BubbleDisplayState.HIDDEN,
      })}
    >
      {chatBubbleDisplayState !== BubbleDisplayState.CLOSED && (
        <Stack sx={bubbleWrapperSx}>
          <IconButton sx={closeButtonSx} onClick={handleOnClose}>
            <CancelIcon />
            <Box sx={cancelBackgroundSx} />
          </IconButton>
          <Card
            data-testid="help-card-button"
            role="button"
            sx={{ ...bubbleSx, ...bubbleSxProps } as SxProps<Theme>}
            elevation={6}
            onClick={onInteract}
          >
            <Typography variant={isMobile ? 'body2' : 'body1'}>
              {text}
            </Typography>
          </Card>
        </Stack>
      )}

      <Badge
        role="button"
        data-testid="chat-bubble-avatar"
        sx={badgeSx({
          isVisible: !hideBadgeOnZeroValue || badgeValue > 0,
        })}
        badgeContent={
          <Typography
            data-testid="chat-badge-value"
            variant="badge"
            sx={badgeValueSx({
              isVisible: !hideBadgeOnZeroValue || badgeValue > 0,
            })}
          >
            {badgeValue}
          </Typography>
        }
        onClick={onInteract}
        {...badgeProps}
      >
        <Avatar
          alt={t<string>('Assistance Chat Button')}
          sx={avatarSx}
          src="/images/chat-face.webp"
        />
      </Badge>
    </Stack>
  )
}

export { AssistanceChatBubble }
